import { TFunction } from 'i18next'
import { toWordsOrdinal } from 'number-to-words'
import { v4 as uuid } from 'uuid'
import { getEncounterAbilityCustomGrouping } from 'app/frontend/types/custom/EncounterAbilityAssignmentDefaults'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import {
  RaidPlanAssignmentEntity,
  RaidPlanAssignmentType,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
  RaidPlanEncounterAssignmentBox,
  RaidPlanEncounterAssignmentCast,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'

export type AssignmentViewMode = 'all_together' | 'alternating' | 'all_separate'

export type AssignmentScope = {
  id: string
  label: string
  castIndexes: number[]
  castGroups: number[][]
}

export type AssignmentScopeIndexes = Omit<AssignmentScope, 'label'>

const customGroupingRegex = /^\d+( \d+)*$/

export const normalizeAbilityDifficulties = (
  difficulty?: Difficulty | Difficulty[] | null,
): Difficulty[] => {
  if (difficulty == null) return []
  return Array.isArray(difficulty) ? difficulty : [difficulty]
}

export const getSoakPrefix = (
  label: string,
  assignmentType?: RaidPlanAssignmentType,
  isCustom = false,
) => {
  if (assignmentType === 'general_assignment') return ''
  if (
    assignmentType === 'interrupt' ||
    assignmentType === 'ranged_interrupt' ||
    assignmentType === 'melee_interrupt'
  ) {
    return label.trim().startsWith('Interrupt ') ? '' : 'Interrupt '
  }
  if (assignmentType === 'area_control') {
    return label.trim().startsWith('CC ') ? '' : 'CC '
  }
  // Custom abilities aren't boss casts, so a "Soak" prefix doesn't
  // make sense for them.
  if (isCustom) return ''

  return label.trim().startsWith('Soak ') ? '' : 'Soak '
}

export const getAbilityReminderText = (
  abilityMessage: string | undefined,
  abilityLabel: string,
  assignmentType?: RaidPlanAssignmentType,
  isCustom = false,
) =>
  abilityMessage?.trim() ||
  `${getSoakPrefix(abilityLabel, assignmentType, isCustom)}${abilityLabel}`

export const getAssignmentReminderText = ({
  message,
  abilityMessage,
  abilityLabel,
  assignmentType,
  isCustom = false,
}: {
  message?: string
  abilityMessage?: string
  abilityLabel: string
  assignmentType?: RaidPlanAssignmentType
  isCustom?: boolean
}): string =>
  message?.trim() ||
  getAbilityReminderText(abilityMessage, abilityLabel, assignmentType, isCustom)

export const sanitizeCustomGroupingInput = (value: string) =>
  value
    .replace(/[^\d ]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^ /, '')

export const normalizeCustomGrouping = (value?: string | null) => {
  if (!value) return undefined
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (!normalized || !customGroupingRegex.test(normalized)) return undefined
  const parsed = normalized.split(' ').map((token) => Number(token))
  if (parsed.some((size) => !Number.isInteger(size) || size < 1)) {
    return undefined
  }
  return parsed.join(' ')
}

export const parseCustomGrouping = (value?: string | null) => {
  const normalized = normalizeCustomGrouping(value)
  if (!normalized) return null
  return normalized.split(' ').map((token) => Number(token))
}

export const getEffectiveAbilityCustomGrouping = (
  spellId: number,
  customGrouping?: string,
) =>
  normalizeCustomGrouping(customGrouping) ??
  normalizeCustomGrouping(getEncounterAbilityCustomGrouping(spellId))

export const getEffectiveAssignmentViewMode = (
  viewMode: AssignmentViewMode | undefined,
  castCount: number,
): AssignmentViewMode =>
  castCount <= 1 ? 'all_together' : viewMode ?? 'alternating'

export const sanitizeExpectedEntitiesInput = (value: string) =>
  value.replace(/[^\d]/g, '')

export const normalizeExpectedEntities = (value?: string | null) => {
  if (!value) return undefined
  const sanitized = sanitizeExpectedEntitiesInput(value)
  if (!sanitized) return undefined
  const parsed = Number(sanitized)
  if (!Number.isInteger(parsed) || parsed < 1) return undefined
  return Math.min(parsed, 40)
}

export const getEffectiveExpectedEntities = (
  abilityExpectedEntities?: number,
  boxExpectedEntities?: number,
) => boxExpectedEntities ?? abilityExpectedEntities

export const roundToOneDecimal = (value: number) => Math.round(value * 10) / 10

export const formatSignedSeconds = (value: number) => {
  const rounded = roundToOneDecimal(value)
  const absolute = Number.isInteger(rounded)
    ? String(Math.abs(rounded))
    : Math.abs(rounded).toFixed(1)
  if (rounded > 0) return `+${absolute}`
  if (rounded < 0) return `-${absolute}`
  return '0'
}

export const sanitizeOneDecimalInput = (
  value: string,
  allowNegative = false,
) => {
  const hasLeadingMinus = allowNegative && value.trimStart().startsWith('-')
  const unsigned = value.replace(/[^\d.]/g, '')
  const [integerPart = '', ...fractionParts] = unsigned.split('.')
  const hasDot = unsigned.includes('.')
  const fractionPart = fractionParts.join('').slice(0, 1)
  const body = hasDot ? `${integerPart}.${fractionPart}` : integerPart
  if (!body || body === '.') {
    if (hasLeadingMinus) return hasDot ? '-.' : '-'
    return hasDot ? '.' : ''
  }
  return hasLeadingMinus ? `-${body}` : body
}

const parseOneDecimal = (
  value: string | number | null | undefined,
  sanitize: (next: string) => string,
) => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return undefined
    return roundToOneDecimal(value)
  }
  if (!value) return undefined
  const sanitized = sanitize(value)
  if (
    !sanitized ||
    sanitized === '-' ||
    sanitized === '.' ||
    sanitized === '-.'
  ) {
    return undefined
  }
  const parsed = Number(sanitized)
  if (!Number.isFinite(parsed)) return undefined
  return roundToOneDecimal(parsed)
}

export const sanitizeCascadeRemindersInput = (value: string) =>
  sanitizeOneDecimalInput(value)

export const normalizeCascadeReminders = (value?: string | number | null) => {
  const parsed = parseOneDecimal(value, sanitizeCascadeRemindersInput)
  if (parsed === undefined || parsed <= 0) return undefined
  return Math.min(parsed, 60)
}

export const sanitizeOffsetRemindersInput = (value: string) =>
  sanitizeOneDecimalInput(value, true)

export const normalizeOffsetReminders = (value?: string | number | null) => {
  const parsed = parseOneDecimal(value, sanitizeOffsetRemindersInput)
  if (parsed === undefined || parsed === 0) return undefined
  return Math.max(-120, Math.min(parsed, 120))
}

export const sanitizeReminderDurationInput = (value: string) =>
  value.replace(/[^\d]/g, '')

export const normalizeReminderDuration = (value?: string | number | null) => {
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value < 1) return undefined
    return Math.min(value, 600)
  }
  if (!value) return undefined
  const sanitized = sanitizeReminderDurationInput(value)
  if (!sanitized) return undefined
  const parsed = Number(sanitized)
  if (!Number.isInteger(parsed) || parsed < 1) return undefined
  return Math.min(parsed, 600)
}

export const getEffectiveReminderOffset = (
  abilityOffset?: number,
  boxOffset?: number,
) => boxOffset ?? abilityOffset

export const getEffectiveReminderDuration = (
  abilityDuration?: number,
  boxDuration?: number,
) => boxDuration ?? abilityDuration

export const getGroupCascadeReminders = (
  assignmentGroups: number,
  abilityCascade?: number,
) =>
  assignmentGroups > 1 ? normalizeCascadeReminders(abilityCascade) : undefined

export const getEntityCascadeReminders = (
  assignmentGroups: number,
  abilityCascade?: number,
  boxCascade?: number,
) => {
  const normalizedBox = normalizeCascadeReminders(boxCascade)
  if (assignmentGroups > 1) return normalizedBox
  return normalizedBox ?? normalizeCascadeReminders(abilityCascade)
}

export const getGroupCascadeReminderSeconds = ({
  assignmentGroups,
  abilityCascade,
  groupIndex,
}: {
  assignmentGroups: number
  abilityCascade?: number
  groupIndex: number
}) => {
  const groupCascade = getGroupCascadeReminders(
    assignmentGroups,
    abilityCascade,
  )
  if (!groupCascade) return undefined
  const seconds = groupIndex * groupCascade
  return seconds > 0 ? seconds : undefined
}

export type ReminderTimeShift = {
  offsetSeconds: number
  cascadeSeconds: number
}

export const getHeaderReminderTimeShift = ({
  assignmentGroups,
  abilityOffset,
  boxOffset,
  abilityCascade,
  groupIndex = 0,
}: {
  assignmentGroups: number
  abilityOffset?: number
  boxOffset?: number
  abilityCascade?: number
  groupIndex?: number
}): ReminderTimeShift => ({
  offsetSeconds: getEffectiveReminderOffset(abilityOffset, boxOffset) ?? 0,
  cascadeSeconds:
    getGroupCascadeReminderSeconds({
      assignmentGroups,
      abilityCascade,
      groupIndex,
    }) ?? 0,
})

export const getChipReminderTimeShift = ({
  assignmentGroups,
  abilityCascade,
  boxCascade,
  entityIndex,
}: {
  assignmentGroups: number
  abilityCascade?: number
  boxCascade?: number
  entityIndex: number
}): ReminderTimeShift | undefined => {
  const entityCascade = getEntityCascadeReminders(
    assignmentGroups,
    abilityCascade,
    boxCascade,
  )
  if (!entityCascade) return undefined
  return {
    offsetSeconds: 0,
    cascadeSeconds: entityIndex * entityCascade,
  }
}

export const toOrdinalWord = (value: number) => {
  if (!Number.isInteger(value) || value < 1) return String(value)
  return toWordsOrdinal(value)
}

export const getCascadedReminderText = (text: string, index: number) =>
  `${text} (${toOrdinalWord(index + 1)})`

export const getCascadedReminderTts = (ttsBase: string, index: number) =>
  `${toOrdinalWord(index + 1)} ${ttsBase}`

export const getEffectiveAssignmentType = (
  assignmentType?: RaidPlanAssignmentType,
): RaidPlanAssignmentType => assignmentType ?? 'general'

export const getAssignmentTypeInterruptRole = (
  assignmentType: RaidPlanAssignmentType,
): 'ranged' | 'melee' | null => {
  if (assignmentType === 'ranged_interrupt') return 'ranged'
  if (assignmentType === 'melee_interrupt') return 'melee'
  return null
}

export const isInterruptAssignmentType = (
  assignmentType: RaidPlanAssignmentType,
) =>
  assignmentType === 'interrupt' ||
  assignmentType === 'ranged_interrupt' ||
  assignmentType === 'melee_interrupt'

export const isImmunityAssignmentType = (
  assignmentType: RaidPlanAssignmentType,
) => assignmentType === 'immunity' || assignmentType === 'mobile_immunity'

export const isAreaControlAssignmentType = (
  assignmentType: RaidPlanAssignmentType,
) => assignmentType === 'area_control'

export const isGeneralAssignmentType = (
  assignmentType: RaidPlanAssignmentType,
) => assignmentType === 'general_assignment'

export const usesNsrtInterruptModule = (
  assignmentType?: RaidPlanAssignmentType,
  nsrtInterruptModule?: boolean,
) =>
  Boolean(nsrtInterruptModule) &&
  isInterruptAssignmentType(getEffectiveAssignmentType(assignmentType))

export const resolveAssignmentViewMode = (
  viewMode: AssignmentViewMode | undefined,
  castCount: number,
  assignmentType?: RaidPlanAssignmentType,
  nsrtInterruptModule?: boolean,
): AssignmentViewMode =>
  usesNsrtInterruptModule(assignmentType, nsrtInterruptModule) ||
  isGeneralAssignmentType(getEffectiveAssignmentType(assignmentType))
    ? 'all_together'
    : getEffectiveAssignmentViewMode(viewMode, castCount)

export const shouldShowAssignmentViewModeTabs = (
  castCount: number,
  assignmentType?: RaidPlanAssignmentType,
  nsrtInterruptModule?: boolean,
) =>
  !isGeneralAssignmentType(getEffectiveAssignmentType(assignmentType)) &&
  !usesNsrtInterruptModule(assignmentType, nsrtInterruptModule) &&
  castCount > 1

export const getAssignmentTargetLabel = (
  abilityLabel: string,
  details: Array<string | false | undefined | null>,
) => {
  const parts = details.filter((part): part is string => Boolean(part))
  return parts.length > 0
    ? `${abilityLabel} (${parts.join(', ')})`
    : abilityLabel
}

export const getEffectiveAssignmentCastCount = (
  entryCount: number,
  assignmentType?: RaidPlanAssignmentType,
) =>
  isGeneralAssignmentType(getEffectiveAssignmentType(assignmentType))
    ? Math.max(entryCount, 1)
    : entryCount

export const getEffectiveAssignmentGroups = (
  defaultAssignmentGroups: number,
  assignmentGroups?: number,
) => {
  if (
    typeof assignmentGroups !== 'number' ||
    !Number.isFinite(assignmentGroups)
  ) {
    return Math.min(Math.max(defaultAssignmentGroups, 1), 20)
  }
  return Math.min(Math.max(Math.floor(assignmentGroups), 1), 20)
}

const normalizeAssignmentEntity = (
  entity: RaidPlanAssignmentEntity,
): RaidPlanAssignmentEntity => ({
  ...entity,
  id: entity.id ?? uuid(),
})

export const normalizeAssignmentBox = (
  box: RaidPlanEncounterAssignmentBox | undefined,
): RaidPlanEncounterAssignmentBox => ({
  id: box?.id ?? uuid(),
  message: box?.message,
  tts_message: box?.tts_message,
  expected_entities: box?.expected_entities,
  rgb: box?.rgb,
  cascade_reminders_by: box?.cascade_reminders_by,
  offset_reminders_by: box?.offset_reminders_by,
  reminder_duration: box?.reminder_duration,
  depends_on: box?.depends_on,
  entities: (box?.entities ?? []).map((entity) =>
    normalizeAssignmentEntity(entity),
  ),
})

export const normalizeAssignmentCast = (
  cast: RaidPlanEncounterAssignmentCast | undefined,
  assignmentGroups: number,
): RaidPlanEncounterAssignmentCast => ({
  id: cast?.id ?? uuid(),
  description: cast?.description,
  boxes: Array.from({ length: Math.max(assignmentGroups, 1) }, (_, boxIndex) =>
    normalizeAssignmentBox(cast?.boxes?.[boxIndex]),
  ),
})

export const getEncounterAssignmentAbilities = (
  timeline: RaidPlanTimeline,
): RaidPlanEncounterAssignmentAbility[] => {
  const abilitiesByKey = timeline.encounter_assignments?.abilities ?? {}
  const abilityOrder = timeline.encounter_assignments?.ability_order ?? []
  const ordered = abilityOrder
    .map((abilityKey) => abilitiesByKey[abilityKey])
    .filter((ability): ability is RaidPlanEncounterAssignmentAbility =>
      Boolean(ability),
    )
  const orderedKeySet = new Set<RaidPlanEncounterAssignmentAbilityKey>(
    abilityOrder,
  )
  const missing = Object.entries(abilitiesByKey)
    .filter(([abilityKey]) => !orderedKeySet.has(abilityKey))
    .map(([, ability]) => ability)
  return [...ordered, ...missing]
}

const GENERAL_ASSIGNMENT_SCOPE: AssignmentScopeIndexes = {
  id: 'all',
  castIndexes: [0],
  castGroups: [[0]],
}

export const getAssignmentScopeIndexes = (
  entries: { time: number }[],
  viewMode: AssignmentViewMode,
  customGrouping?: string,
  assignmentType?: RaidPlanAssignmentType,
): AssignmentScopeIndexes[] => {
  if (isGeneralAssignmentType(getEffectiveAssignmentType(assignmentType))) {
    return [GENERAL_ASSIGNMENT_SCOPE]
  }
  if (!entries.length) return []
  const hasCustomGrouping = Boolean(parseCustomGrouping(customGrouping))
  const castGroups = buildAssignmentCastGroups(entries, customGrouping)
  const effectiveViewMode = entries.length <= 1 ? 'all_together' : viewMode
  if (effectiveViewMode === 'all_together') {
    return [
      {
        id: 'all',
        castIndexes: entries.map((_, index) => index),
        castGroups,
      },
    ]
  }
  if (effectiveViewMode === 'alternating') {
    const oddCastGroups = castGroups.filter((_, index) => index % 2 === 0)
    const evenCastGroups = castGroups.filter((_, index) => index % 2 === 1)
    return [
      {
        id: 'odd',
        castIndexes: oddCastGroups.flat(),
        castGroups: oddCastGroups,
      },
      {
        id: 'even',
        castIndexes: evenCastGroups.flat(),
        castGroups: evenCastGroups,
      },
    ].filter((scope) => scope.castIndexes.length > 0)
  }
  return castGroups.map((castGroup, index) => ({
    id: hasCustomGrouping ? `group-${index}` : `cast-${index}`,
    castIndexes: castGroup,
    castGroups: [castGroup],
  }))
}

export const getAssignmentScopes = (
  entries: { time: number }[],
  viewMode: AssignmentViewMode,
  t: TFunction,
  customGrouping?: string,
  assignmentType?: RaidPlanAssignmentType,
  // Custom abilities aren't tied to boss casts, so their scopes read as
  // "reminders" for users while the underlying grouping stays cast-based.
  isCustom = false,
): AssignmentScope[] => {
  const scopes = getAssignmentScopeIndexes(
    entries,
    viewMode,
    customGrouping,
    assignmentType,
  )
  return scopes.map((scope, scopeIndex) => {
    if (isGeneralAssignmentType(getEffectiveAssignmentType(assignmentType))) {
      return {
        ...scope,
        label: '',
      }
    }
    if (scope.id === 'all') {
      const isSingleCast = scope.castIndexes.length === 1
      const singleCastKey = isCustom
        ? 'dashboard.team.plans.assignments.reminder-at'
        : 'dashboard.team.plans.assignments.cast-at'
      const allCastsKey = isCustom
        ? 'dashboard.team.plans.assignments.view-modes.reminders'
        : 'dashboard.team.plans.assignments.view-modes.casts'
      return {
        ...scope,
        label: t(isSingleCast ? singleCastKey : allCastsKey),
      }
    }
    if (scope.id === 'odd') {
      return {
        ...scope,
        label: t(
          isCustom
            ? 'dashboard.team.plans.assignments.view-modes.odd-reminders'
            : 'dashboard.team.plans.assignments.view-modes.odd',
        ),
      }
    }
    if (scope.id === 'even') {
      return {
        ...scope,
        label: t(
          isCustom
            ? 'dashboard.team.plans.assignments.view-modes.even-reminders'
            : 'dashboard.team.plans.assignments.view-modes.even',
        ),
      }
    }
    return {
      ...scope,
      label: t(
        isCustom
          ? 'dashboard.team.plans.assignments.reminder-label'
          : 'dashboard.team.plans.assignments.cast-label',
        {
          count: scopeIndex + 1,
        },
      ),
    }
  })
}

const buildAssignmentCastGroups = (
  entries: { time: number }[],
  customGrouping?: string,
) => {
  const grouping = parseCustomGrouping(customGrouping)
  if (!grouping) {
    return entries.map((_, index) => [index])
  }
  const castGroups: number[][] = []
  let castIndex = 0
  let groupIndex = 0
  while (castIndex < entries.length) {
    const groupSize = grouping[groupIndex % grouping.length]
    const nextCastIndex = Math.min(castIndex + groupSize, entries.length)
    castGroups.push(
      Array.from(
        { length: nextCastIndex - castIndex },
        (_, index) => castIndex + index,
      ),
    )
    castIndex = nextCastIndex
    groupIndex += 1
  }
  return castGroups
}
